import { getSupabase } from '@/lib/supabase';

export interface ActivityEvent {
  id: string;
  client_id: string;
  coach_id: string;
  client_program_id: string;
  type:
    | 'workout_completed'
    | 'workout_missed'
    | 'client_checkin'
    | 'tracker_logged'
    | 'goal_progress'
    | 'nutrition_request'
    | 'client_message'
    | 'goal_updated'
    | 'goal_created'
    | 'goal_completed'
    | 'program_updated'
    | 'program_assigned'
    | 'nutrition_updated'
    | 'nutrition_assigned'
    | 'tracker_assigned'
    | 'supplement_assigned'
    | 'supplement_removed'
    | 'supplement_updated'
    | 'form_assigned'
    | 'form_removed';
  summary: string;
  details: any;
  priority: number;
  created_at: string;
  seen_at: string | null;
  dismissed_at: string | null;
  actor_id: string; // ID of who performed the action
}

export interface ActivityEventWithClient extends ActivityEvent {
  client_name?: string;
  client_initials?: string;
}

/**
 * Get activity log for a specific client (COACH ACTIVITIES for client to see)
 * This shows activities where the COACH is the actor (coach did something for the client)
 */
export async function getClientActivityLog(
  clientId: string,
  days: number = 7,
  limit: number = 50,
): Promise<ActivityEvent[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  if (!clientId) return [];

  const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  // Get all activities for this client first
  const { data, error } = await sb
    .from('client_events')
    .select('*')
    .eq('client_id', clientId)
    .gte('created_at', cutoffDate)
    .order('created_at', { ascending: false })
    .limit(limit * 2); // Get more to account for filtering

  if (error) {
    console.error('Failed to fetch client activity log:', error);
    throw error;
  }

  // Filter to only show coach activities (where actor_id = coach_id)
  const coachActivities = (data || []).filter((event) => event.actor_id === event.coach_id).slice(0, limit); // Apply limit after filtering

  return coachActivities;
}

/**
 * Get activity feed for a coach (CLIENT ACTIVITIES for coach to see)
 * This shows activities where the CLIENT is the actor (client did something)
 */
export async function getCoachActivityFeed(
  coachId: string,
  days: number = 30,
  limit: number = 100,
): Promise<ActivityEventWithClient[]> {
  if (!coachId) return [];

  try {
    // Load via Worker (BFF pattern for httpOnly cookie auth)
    const eventsRes = await fetch(`/api/clients/events?days=${days}&limit=${limit * 2}`, {
      credentials: 'include',
    });

    if (!eventsRes.ok) {
      console.error('[activity-events] Worker endpoint failed:', eventsRes.status);
      return [];
    }

    const { items: events } = await eventsRes.json();

    if (!events || events.length === 0) {
      return [];
    }

    // Filter to only show client activities (where actor_id = client_id)
    // This means the CLIENT performed the action (for coach to see)
    const clientActivities = events.filter((event: any) => event.actor_id === event.client_id).slice(0, limit); // Apply limit after filtering

    // Get unique client IDs from events
    const _clientIds = [...new Set(clientActivities.map((e: any) => e.client_id))];

    // Get client names via Worker
    const clientsRes = await fetch('/api/coach/clients', { credentials: 'include' });
    const clientNameMap = new Map();

    if (clientsRes.ok) {
      const { items: clients } = await clientsRes.json();
      (clients || []).forEach((client: any) => {
        const clientName = client.name || client.email || 'Unknown Client';
        clientNameMap.set(client.id, clientName);
      });
    }

    // Transform data to include client names
    const result = clientActivities.map((event: any) => ({
      ...event,
      client_name: clientNameMap.get(event.client_id) || 'Unknown Client',
      client_initials: getInitials(clientNameMap.get(event.client_id) || 'UC'),
    }));

    return result;
  } catch (err) {
    console.error('Failed to fetch coach activity feed:', err);
    return [];
  }
}

/**
 * Get activity events by type for a client
 */
export async function getClientActivityByType(
  clientId: string,
  types: string[],
  days: number = 7,
  limit: number = 50,
): Promise<ActivityEvent[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await sb
    .from('client_events')
    .select('*')
    .eq('client_id', clientId)
    .in('type', types)
    .gte('created_at', cutoffDate)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Failed to fetch client activity by type:', error);
    throw error;
  }

  return data || [];
}

/**
 * Mark activity event as seen
 */
export async function markActivitySeen(eventId: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { error } = await sb.from('client_events').update({ seen_at: new Date().toISOString() }).eq('id', eventId);

  if (error) {
    console.error('Failed to mark activity as seen:', error);
    throw error;
  }
}

/**
 * Mark activity event as dismissed
 */
export async function dismissActivity(eventId: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { error } = await sb.from('client_events').update({ dismissed_at: new Date().toISOString() }).eq('id', eventId);

  if (error) {
    console.error('Failed to dismiss activity:', error);
    throw error;
  }
}

/**
 * Create a client message activity event
 */
export async function createMessageActivity(
  clientId: string,
  coachId: string,
  messagePreview: string,
  messageId?: string,
): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  // Use the create_activity_event function we created in SQL
  const { error } = await sb.rpc('create_activity_event', {
    p_client_id: clientId,
    p_coach_id: coachId,
    p_type: 'client_message',
    p_summary: `New message: ${messagePreview.substring(0, 50)}${messagePreview.length > 50 ? '...' : ''}`,
    p_details: {
      message_id: messageId,
      preview: messagePreview.substring(0, 100),
      created_at: new Date().toISOString(),
    },
    p_priority: 0,
  });

  if (error) {
    console.error('Failed to create message activity:', error);
    throw error;
  }
}

/**
 * Manually trigger missed workout detection
 */
export async function detectMissedWorkouts(clientId?: string): Promise<number> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb.rpc('detect_missed_workouts', {
    p_client_id: clientId || null,
  });

  if (error) {
    console.error('Failed to detect missed workouts:', error);
    throw error;
  }

  return data || 0;
}

/**
 * Get activity statistics for a client
 */
export async function getClientActivityStats(
  clientId: string,
  days: number = 7,
): Promise<{
  total: number;
  workoutsCompleted: number;
  workoutsMissed: number;
  trackersLogged: number;
  goalsUpdated: number;
  messagesReceived: number;
}> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await sb
    .from('client_events')
    .select('type')
    .eq('client_id', clientId)
    .gte('created_at', cutoffDate);

  if (error) {
    console.error('Failed to fetch activity stats:', error);
    throw error;
  }

  const events = data || [];
  const stats = {
    total: events.length,
    workoutsCompleted: events.filter((e) => e.type === 'workout_completed').length,
    workoutsMissed: events.filter((e) => e.type === 'workout_missed').length,
    trackersLogged: events.filter((e) => e.type === 'tracker_logged').length,
    goalsUpdated: events.filter((e) => e.type === 'goal_progress').length,
    messagesReceived: events.filter((e) => e.type === 'client_message').length,
  };

  return stats;
}

/**
 * Get recent activity summary for coach dashboard
 */
export async function getCoachActivitySummary(
  coachId: string,
  days: number = 7,
): Promise<{
  totalActivities: number;
  activeClients: number;
  workoutsCompleted: number;
  missedWorkouts: number;
  newMessages: number;
}> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await sb
    .from('client_events')
    .select('type, client_id')
    .eq('coach_id', coachId)
    .gte('created_at', cutoffDate);

  if (error) {
    console.error('Failed to fetch coach activity summary:', error);
    throw error;
  }

  const events = data || [];
  const uniqueClients = new Set(events.map((e) => e.client_id));

  return {
    totalActivities: events.length,
    activeClients: uniqueClients.size,
    workoutsCompleted: events.filter((e) => e.type === 'workout_completed').length,
    missedWorkouts: events.filter((e) => e.type === 'workout_missed').length,
    newMessages: events.filter((e) => e.type === 'client_message').length,
  };
}

/**
 * Helper function to get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/**
 * Get activity icon type for rendering
 */
export function getActivityIcon(type: string): string {
  switch (type) {
    case 'workout_completed':
      return 'check-circle';
    case 'workout_missed':
      return 'x-circle';
    case 'client_checkin':
      return 'file-text';
    case 'tracker_logged':
    case 'tracker_assigned':
      return 'trending-up';
    case 'goal_progress':
    case 'goal_updated':
    case 'goal_created':
      return 'target';
    case 'goal_completed':
      return 'trophy';
    case 'client_message':
      return 'message-circle';
    case 'nutrition_request':
      return 'utensils';
    case 'nutrition_updated':
    case 'nutrition_assigned':
      return 'apple';
    case 'program_updated':
    case 'program_assigned':
      return 'activity';
    case 'supplement_assigned':
    case 'supplement_removed':
    case 'supplement_updated':
      return 'pill';
    case 'form_assigned':
    case 'form_removed':
      return 'file-text';
    default:
      return 'activity';
  }
}

/**
 * Get activity color for rendering
 */
export function getActivityColor(type: string): string {
  switch (type) {
    case 'workout_completed':
      return 'text-green-500';
    case 'workout_missed':
      return 'text-red-500';
    case 'client_checkin':
      return 'text-indigo-500';
    case 'tracker_logged':
    case 'tracker_assigned':
      return 'text-blue-500';
    case 'goal_progress':
    case 'goal_updated':
    case 'goal_created':
      return 'text-purple-500';
    case 'goal_completed':
      return 'text-yellow-500';
    case 'client_message':
      return 'text-cyan-500';
    case 'nutrition_request':
    case 'nutrition_updated':
    case 'nutrition_assigned':
      return 'text-orange-500';
    case 'program_updated':
    case 'program_assigned':
      return 'text-indigo-500';
    case 'supplement_assigned':
    case 'supplement_updated':
      return 'text-teal-500';
    case 'supplement_removed':
      return 'text-rose-500';
    case 'form_assigned':
      return 'text-emerald-500';
    case 'form_removed':
      return 'text-rose-500';
    default:
      return 'text-muted-foreground';
  }
}
